/**
 * «Önizle» — `openTemplatePreview` (`m08-admin.js`).
 *
 * A wide dialog holding one thing: the layout drawn through the shared render engine with SAMPLE
 * values, never with a real recipient's (rule 12). The two chips swap the holder field between the
 * short name and the deliberately over-long one, which is how the reference demonstrates
 * shrink-to-fit — the long name is not decorative, it is the test case, so it stays exactly as
 * long as `sampleData` writes it.
 *
 * The dialog takes the layout as a prop rather than reading the editor's state: upstream's second
 * argument is what separates the two call sites. The template grid calls `openTemplatePreview(t)`
 * and gets the published release through `layoutOf`; the editor calls it with `ED.layout` and gets
 * its own UNSAVED working copy. Both land here.
 */
import { type ReactElement, useState } from 'react';
import { useSettings } from '../../data/store';
import type { DemoTemplate } from '../../data/types';
import {
  canvasH,
  canvasW,
  type Layout,
  layoutOf,
  previewSVG,
  type SampleMode,
  sampleData,
} from '../../lib/render';
import { Modal, ModalHead } from '../../ui/Modal';
import type { EditorLayout } from './contract';

export interface TemplatePreviewProps {
  template: DemoTemplate;
  /** `openTemplatePreview(t, layout)`'s optional second argument; omitted, the release is drawn. */
  layout?: EditorLayout | undefined;
  /**
   * The language the drawing is in. Passed by the editor, which hands in a layout already reduced to
   * that language (`localizedEditorLayout`); it reaches `sampleData` so the AUTO `credential_title`
   * is that language's name rather than always the Turkish one.
   */
  lang?: 'tr' | 'en' | undefined;
  onClose: () => void;
}

export function TemplatePreview({ template, layout, lang, onClose }: TemplatePreviewProps): ReactElement {
  const settings = useSettings();
  const [mode, setMode] = useState<SampleMode>('short');

  /* `layoutOf` already returns the engine's shape; the editor's working copy is the same object
     graph with the editor's own field types over it. */
  const lay: Layout = (layout as unknown as Layout | undefined) ?? layoutOf(template);
  /* The sample QR has to resolve to this workspace's public base, as `DB.settings.publicBase`. */
  const svg = previewSVG(
    lay,
    sampleData(template, mode, lay, { publicBase: settings.publicBase, ...(lang ? { lang } : {}) }),
  );

  return (
    <Modal label="Şablon önizleme" wide onClose={onClose}>
      <ModalHead title={`${template.name} · önizleme`} onClose={onClose} />
      <div className="card-pad">
        <div
          style={{ background: 'var(--surface-3)', padding: '16px', borderRadius: '6px' }}
          id="tpPrev"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {/* The reference marks neither chip as active; the selected mode is read off the drawing. */}
        <div className="chips" style={{ marginTop: '10px' }}>
          <button className="chip" type="button" data-pm="short" onClick={() => setMode('short')}>
            Kısa değer
          </button>
          <button className="chip" type="button" data-pm="long" onClick={() => setMode('long')}>
            Çok uzun değer
          </button>
        </div>
        <p className="small muted" style={{ marginTop: '8px' }}>
          {`Uzun değer, katmanın minimum puntosuna kadar otomatik küçültülür (shrink-to-fit). Canvas ${canvasW(lay.canvas)}×${canvasH(lay.canvas)} · ${lay.canvas.orientation}.`}
        </p>
      </div>
    </Modal>
  );
}
